import { ReviewPanel, AgentEmptyState } from '../ReviewPanel';
import type { AgentOutputRow } from '@/hooks/useJobProgress';

interface ForgeBatchPayload {
  batch_index?: number;
  files?: { path: string; contents: string }[];
  batch_complete?: boolean;
}

interface ForgeFinalPayload {
  total_batches?: number;
  total_files?: number;
  zip_bytes?: number;
}

interface LexReviewPayload {
  pass?: boolean;
  violations?: { id: string; severity: string }[];
}

interface ForgePanelProps {
  forgeOutputs: AgentOutputRow[];
  lexOutputs: AgentOutputRow[];
  isRunning?: boolean;
}

export function ForgePanel({ forgeOutputs, lexOutputs, isRunning }: ForgePanelProps) {
  if (forgeOutputs.length === 0) {
    return (
      <ReviewPanel agent="forge" index={7}>
        <AgentEmptyState
          agent="forge"
          isRunning={isRunning}
          idleMessage="FORGE hasn't started yet."
        />
      </ReviewPanel>
    );
  }

  const finalRow = forgeOutputs.find((o) => o.itemKey === 'final');
  const batchRows = forgeOutputs.filter((o) => o.itemKey?.startsWith('batch_'));
  const lexCodeRows = lexOutputs.filter((o) => o.itemKey?.startsWith('forge_batch_'));

  return (
    <ReviewPanel
      agent="forge"
      index={7}
      subtitle="Code generation"
      rightTag={`${batchRows.length} batch${batchRows.length === 1 ? '' : 'es'}`}
    >
      <p className="text-body text-graphite mb-4">
        FORGE produces the deployable Vite + React + Tailwind project in internal batches. LEX audits
        each batch and FORGE revises if needed before the next batch starts.
      </p>

      <ol className="space-y-3">
        {batchRows.map((b) => {
          const payload = b.payload as ForgeBatchPayload;
          const lexRow = lexCodeRows.find((l) => l.itemKey === `forge_batch_${payload.batch_index}`);
          const lex = lexRow?.payload as LexReviewPayload | undefined;
          const fileCount = payload.files?.length ?? 0;
          return (
            <li key={b._id} className="rounded-sm border border-ink/10 p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-[11px] text-graphite tabular-nums">
                    batch {String(payload.batch_index ?? 0).padStart(2, '0')}
                  </span>
                  <span className="font-mono text-[11px] text-ink">{fileCount} file{fileCount === 1 ? '' : 's'}</span>
                </div>
                {lex ? (
                  <span className={`font-mono text-[10px] ${lex.pass ? 'text-moss' : 'text-vermilion'}`}>
                    LEX: {lex.pass ? 'pass' : `${lex.violations?.length ?? 0} violation${lex.violations?.length === 1 ? '' : 's'}`}
                  </span>
                ) : (
                  <span className="font-mono text-[10px] text-graphite">LEX pending</span>
                )}
              </div>
              {payload.files && payload.files.length > 0 && (
                <ul className="mt-2 space-y-0.5">
                  {payload.files.slice(0, 5).map((f) => (
                    <li key={f.path} className="font-mono text-[10px] text-graphite truncate">
                      {f.path}
                    </li>
                  ))}
                  {payload.files.length > 5 && (
                    <li className="font-mono text-[10px] text-graphite italic">
                      … {payload.files.length - 5} more
                    </li>
                  )}
                </ul>
              )}
            </li>
          );
        })}
      </ol>

      {finalRow && (
        <div className="mt-6 rounded-sm border border-moss/30 bg-moss/5 p-4">
          <p className="smallcaps text-moss">Build complete</p>
          <p className="mt-1 text-body text-ink">
            {(finalRow.payload as ForgeFinalPayload).total_files ?? '—'} files across{' '}
            {(finalRow.payload as ForgeFinalPayload).total_batches ?? '—'} batches.
            Zip:{' '}
            {humanBytes((finalRow.payload as ForgeFinalPayload).zip_bytes ?? 0)}.
          </p>
        </div>
      )}
    </ReviewPanel>
  );
}

function humanBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}
